import {
  createUnifiedTheme,
  palettes,
  PageTheme,
  UnifiedThemeOptions,
} from '@backstage/theme';

// HeroDevs brand tokens from the HeroDevs design system. The dark surfaces and
// the selected-item wash come from the NES Portal design system, because the
// main system has no full dark theme. The README links both.
const brand = {
  pink300: '#F85EEB',
  pink400: '#F926E1',
  pink500: '#D10DBF',
  pink600: '#A50896',
  blue300: '#55C4FC',
  blue400: '#2AB5FB',
  blue600: '#0678B0',
  neutral0: '#FFFFFF',
  neutral200: '#DCDCDC',
  neutral300: '#C4C4C4',
  neutral400: '#A0A0A0',
  neutral500: '#737373',
  neutral600: '#525252',
  neutral800: '#1F1F1F',
  neutral950: '#080808',
  portalSurfaceDark: '#171717',
  portalHairlineDark: '#262626',
  portalBorderDark: '#303030',
  portalAccentWashDark: 'rgba(249, 38, 225, 0.12)',
};

// The design system defines only the 500 steps of these status hues and flags
// them as unconfirmed with brand. The other steps are derived from them. Light
// mode uses darker steps where the 500 step is too faint on white, and dark
// mode uses the lighter 400 steps throughout.
const status = {
  success400: '#2FB570',
  success700: '#0F6B39',
  warning400: '#F6B032',
  warning700: '#A6680C',
  error400: '#F55A60',
  error600: '#D62830',
};

// We bundle Manrope Regular to Bold only, so the headings the design system
// sets at 800 use 700 here.
const typography: UnifiedThemeOptions['typography'] = {
  htmlFontSize: 16,
  fontFamily:
    '"Manrope", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  h1: { fontSize: 48, fontWeight: 700, marginBottom: 10 },
  h2: { fontSize: 36, fontWeight: 700, marginBottom: 8 },
  h3: { fontSize: 28, fontWeight: 700, marginBottom: 6 },
  h4: { fontSize: 22, fontWeight: 700, marginBottom: 6 },
  h5: { fontSize: 18, fontWeight: 600, marginBottom: 4 },
  h6: { fontSize: 14, fontWeight: 700, marginBottom: 2 },
};

// A flat, dark header for every page type, replacing Backstage's colored
// wave artwork. The sidebar logo already carries the brand gradient, which
// the design system allows once per view.
const header: PageTheme = {
  colors: [brand.neutral800],
  shape: 'none',
  backgroundImage: `linear-gradient(${brand.neutral800}, ${brand.neutral800})`,
  fontColor: brand.neutral0,
};

// The sidebar stays dark in both modes, so it shares one set of colors.
const sidebar = {
  navigation: {
    background: brand.neutral950,
    indicator: brand.pink400,
    color: brand.neutral300,
    selectedColor: brand.neutral0,
    navItem: { hoverBackground: brand.neutral800 },
    submenu: { background: brand.neutral800 },
  },
  pinSidebarButton: { icon: brand.neutral950, background: brand.neutral300 },
};

// Each entry replaces Backstage's default entry for that component instead of
// merging with it, so the Backstage defaults for MuiButton, MuiPaper, and
// MuiTableRow are repeated below.
const components: UnifiedThemeOptions['components'] = {
  BackstageHeader: {
    styleOverrides: {
      header: { boxShadow: 'none' },
    },
  },
  BackstageSidebar: {
    styleOverrides: {
      drawer: { borderRight: `1px solid ${brand.portalHairlineDark}` },
    },
  },
  BackstageSidebarItem: {
    styleOverrides: {
      selected: { background: brand.portalAccentWashDark },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600 },
      text: { padding: undefined },
    },
  },
  // Light pages and cards are both white, so a hairline separates table rows
  // instead of Backstage's alternating row tint.
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
      hover: { '&:hover': { cursor: 'pointer' } },
      head: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },
  // Flat-first: resting cards get a hairline border instead of a shadow.
  // Menus and dialogs use higher elevations and keep theirs.
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'unset' },
      elevation1: ({ theme }) => ({
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
};

export const herodevsLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    ...sidebar,
    primary: {
      main: brand.pink500,
      dark: brand.pink600,
      contrastText: brand.neutral0,
    },
    secondary: { main: brand.blue600, contrastText: brand.neutral0 },
    error: { main: status.error600 },
    warning: { main: status.warning700 },
    success: { main: status.success700 },
    info: { main: brand.blue600 },
    // White pages, as in the design system. Pink-500 links fall below AA on
    // its neutral-50 alternative.
    background: { default: brand.neutral0, paper: brand.neutral0 },
    text: {
      primary: brand.neutral950,
      secondary: brand.neutral600,
      disabled: brand.neutral400,
    },
    divider: brand.neutral200,
    status: {
      ok: status.success700,
      warning: status.warning700,
      error: status.error600,
      running: brand.blue600,
      pending: brand.neutral500,
      aborted: brand.neutral600,
    },
    border: brand.neutral200,
    textContrast: brand.neutral950,
    textVerySubtle: brand.neutral200,
    textSubtle: brand.neutral600,
    link: brand.pink500,
    linkHover: brand.pink600,
    tabbar: { indicator: brand.pink500 },
  },
  pageTheme: { home: header },
  typography,
  components,
});

export const herodevsDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    ...sidebar,
    // White text on pink-400 fails AA, so dark-mode buttons use dark text,
    // unlike the NES Portal system. Hover lightens instead of darkening.
    primary: {
      main: brand.pink400,
      dark: brand.pink300,
      contrastText: brand.neutral950,
    },
    secondary: { main: brand.blue300, contrastText: brand.neutral950 },
    error: { main: status.error400 },
    warning: { main: status.warning400 },
    success: { main: status.success400 },
    info: { main: brand.blue400 },
    background: {
      default: brand.neutral950,
      paper: brand.portalSurfaceDark,
    },
    text: {
      primary: brand.neutral0,
      secondary: brand.neutral400,
      disabled: brand.neutral600,
    },
    divider: brand.portalHairlineDark,
    status: {
      ok: status.success400,
      warning: status.warning400,
      error: status.error400,
      running: brand.blue300,
      pending: status.warning400,
      aborted: brand.neutral400,
    },
    border: brand.portalBorderDark,
    textContrast: brand.neutral0,
    textVerySubtle: brand.neutral500,
    textSubtle: brand.neutral400,
    link: brand.pink400,
    linkHover: brand.pink300,
    tabbar: { indicator: brand.pink400 },
  },
  pageTheme: { home: header },
  typography,
  components,
});
